import { z } from "zod";

export const songPlaybackSchema = z.object({
  manifestUrl: z.string().url(),
  expiresAt: z.string().datetime(),
});
export type SongPlayback = z.infer<typeof songPlaybackSchema>;

export const songAppLinkSchema = z.object({
  url: z.string().url(),
});
export type SongAppLink = z.infer<typeof songAppLinkSchema>;

export const playPlatformSchema = z.enum([
  "ios",
  "android",
  "web",
  "link_in_bio",
  "partner",
]);
export const playEventSchema = z
  .object({
    play_id: z.uuidv4(),
    seq: z.number().int().min(0).max(2147483647),
    kind: z.enum(["start", "heartbeat", "stop"]),
    client_ts: z.iso.datetime({ offset: true }),
    position: z.number().min(0).max(99999999.99),
    song_id: z.uuid().optional(),
    collection: z.string().max(128).nullable().optional(),
    country_code: z.string().length(2).optional(),
    reason: z
      .enum(["track_change", "queue_end", "pause", "user", "unload"])
      .optional(),
    seeked: z.boolean().optional(),
    token: z.string().max(2048).optional(),
  })
  .strict()
  .superRefine((event, ctx) => {
    if (event.kind === "start" && !event.song_id)
      ctx.addIssue({
        code: "custom",
        message: "song_id required on start",
        path: ["song_id"],
      });
    if (
      event.kind !== "start" &&
      (event.song_id !== undefined ||
        event.collection !== undefined ||
        event.country_code !== undefined)
    )
      ctx.addIssue({ code: "custom", message: "start-only metadata" });
  });
// Validate envelopes here and individual events in the operation so a malformed
// event never prevents other plays in the batch from being applied.
export const playEventsRequestSchema = z
  .object({
    device_id: z.string().min(1).max(128),
    platform: playPlatformSchema,
    events: z
      .array(z.unknown())
      .min(1)
      .max(5000, "too_many_events: maximum 5000 events per request"),
  })
  .strict();
export const playEventsResponseSchema = z.object({
  results: z.array(
    z.object({
      play_id: z.string(),
      seq: z.number(),
      accepted: z.boolean(),
      duplicate: z.boolean(),
      error: z.string().optional(),
    }),
  ),
  plays: z.record(
    z.string(),
    z.object({
      token: z.string(),
      last_seq: z.number().int(),
      interval_seconds: z.number().int().positive(),
    }),
  ),
});
export type PlayEvent = z.infer<typeof playEventSchema>;
export type PlayEventsRequest = z.infer<typeof playEventsRequestSchema>;
export type PlayEventsResponse = z.infer<typeof playEventsResponseSchema>;

export const playStatusSchema = z.enum([
  "valid",
  "suspicious",
  "quarantined",
  "confirmed",
]);
export type PlayStatus = z.infer<typeof playStatusSchema>;
